// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useEffect, useMemo } from 'react';
import {
  webLightTheme,
  webDarkTheme,
  type Theme,
} from '@fluentui/react-components';

export type ThemePreference = 'system' | 'light' | 'dark';

const DARK_MQ = '(prefers-color-scheme: dark)';

/**
 * Resolves a user's theme preference to a Fluent UI Theme object.
 * When preference is 'system', listens for OS colour-scheme changes in real time.
 * Also sets document-level hints (color-scheme, body background) to prevent flash.
 */
export function useResolvedTheme(preference: ThemePreference): Theme {
  const [osDark, setOsDark] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(DARK_MQ).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(DARK_MQ);
    const handler = (e: MediaQueryListEvent) => { setOsDark(e.matches); };
    mql.addEventListener('change', handler);
    // Sync in case it changed between render and effect
    setOsDark(mql.matches);
    return () => { mql.removeEventListener('change', handler); };
  }, []);

  const isDark =
    preference === 'dark' || (preference === 'system' && osDark);

  const theme = useMemo<Theme>(
    () => (isDark ? webDarkTheme : webLightTheme),
    [isDark],
  );

  // Keep document-level hints in sync to prevent body flash and style native scrollbars
  useEffect(() => {
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
    document.body.style.backgroundColor = isDark
      ? webDarkTheme.colorNeutralBackground1
      : webLightTheme.colorNeutralBackground1;
  }, [isDark]);

  return theme;
}
